import {
  AlignJustify,
  CheckCircle2,
  Eye,
  Focus,
  Highlighter,
  MessageSquareQuote,
  PanelTop,
  Type,
} from 'lucide-react';
import {
  ChoiceCards,
  Segmented,
  SettingsCard,
  SettingsPageHeader,
  SettingsRow,
  SettingsScopeNote,
  Toggle,
} from './SettingsPrimitives';
import PreferenceSyncNotice from './PreferenceSyncNotice';
import usePreferenceNamespace from './usePreferenceNamespace';

const BACKGROUNDS = [
  { value: 'workspace', label: '工作台', description: '与整体界面一致', icon: PanelTop },
  { value: 'paper', label: '纸张', description: '明亮、适合逐字修改', icon: Type },
  { value: 'warm', label: '暖色护眼', description: '长时间阅读更柔和', icon: Eye },
];

const EDITOR_DEFAULTS = {
  font: 'system',
  fontSize: 18,
  lineHeight: 1.9,
  background: 'paper',
  dialogueHighlight: true,
  highlightColor: 'lavender',
  formatHints: 'standard',
  confirmClose: true,
  focusMode: false,
};

export default function EditorSettingsPanel({ preview = false, english = false }) {
  const {
    values,
    status,
    statusLabel,
    updatePreference,
    retry,
  } = usePreferenceNamespace('editor', EDITOR_DEFAULTS, { enabled: !preview });
  const {
    font,
    fontSize,
    lineHeight,
    background,
    dialogueHighlight,
    highlightColor,
    formatHints,
    confirmClose,
    focusMode,
  } = values;

  return (
    <div className={`account-content__stack creation-settings-page${status === 'loading' ? ' is-loading' : ''}`}>
      <SettingsPageHeader
        eyebrow="EDITOR"
        title={english ? 'Editor Settings' : '编辑器设置'}
        description={english ? 'These options change reading and editing only. Script content and export formatting stay untouched.' : '这些选项只改变阅读和编辑体验，不修改剧本正文或最终导出格式。'}
        status={english ? ({ preview: 'Personal settings', loading: 'Loading', ready: 'Synced', saving: 'Saving', saved: 'Saved', conflict: 'Review needed', error: '保存失败' }[status]) : statusLabel}
      />
      <PreferenceSyncNotice
        preview={preview}
        status={status}
        onRetry={retry}
        previewText={english ? 'Preview changes on the right. Your script content will not be modified.' : '在右边实时看看效果，当前选择不会改动剧本正文。'}
        english={english}
      />

      <div className="creation-editor-layout">
        <div className="account-content__stack">
          <SettingsCard eyebrow="TYPOGRAPHY" title={english ? 'Reading Typography' : '阅读排版'} aside={english ? 'Personal' : '个人'}>
            <SettingsRow icon={Type} title={english ? 'Reading font' : '阅读字体'} description={english ? 'Font used in the script area.' : '正文区域使用的字体。'}>
              <Segmented
                label={english ? 'Reading font' : '阅读字体'}
                value={font}
                options={[
                  { value: 'system', label: english ? 'System' : '系统' },
                  { value: 'serif', label: english ? 'Serif' : '宋体' },
                  { value: 'wenkai', label: english ? 'WenKai' : '文楷' },
                ]}
                onChange={value => updatePreference('font', value)}
              />
            </SettingsRow>
            <SettingsRow icon={Type} title={english ? 'Script font size' : '正文字号'} description={english ? 'Affects editor display only.' : '仅影响编辑器显示。'}>
              <Segmented
                label={english ? 'Script font size' : '正文字号'}
                value={fontSize}
                options={[16, 18, 20, 22].map(value => ({ value, label: `${value}px` }))}
                onChange={value => updatePreference('fontSize', value)}
              />
            </SettingsRow>
            <SettingsRow icon={AlignJustify} title={english ? 'Line height' : '正文行高'} description={english ? 'Give script lines room to breathe during long reading sessions.' : '给剧本分行留足呼吸，长时间阅读也不拥挤。'}>
              <Segmented
                label={english ? 'Line height' : '正文行高'}
                value={lineHeight}
                options={[1.6, 1.8, 1.9, 2.1]}
                onChange={value => updatePreference('lineHeight', value)}
              />
            </SettingsRow>
          </SettingsCard>

          <SettingsCard eyebrow="READING SURFACE" title={english ? 'Reading Surface' : '阅读背景'} aside={english ? 'Personal' : '个人'}>
            <ChoiceCards
              label={english ? 'Editor background' : '编辑器背景'}
              value={background}
              options={english ? BACKGROUNDS.map((option, index) => ({ ...option, label: ['Workspace', 'Paper', 'Warm comfort'][index], description: ['Matches the interface', 'Bright and precise', 'Softer for long reading'][index] })) : BACKGROUNDS}
              onChange={value => updatePreference('background', value)}
            />
          </SettingsCard>

          <SettingsCard eyebrow="WRITING ASSIST" title={english ? 'Writing Assistance' : '编辑辅助'} aside={english ? 'Personal' : '个人'}>
            <SettingsRow
              icon={MessageSquareQuote}
              title={english ? 'Dialogue highlighting' : '对话高亮'}
              description={english ? 'Make dialogue easier to distinguish from action descriptions.' : '让角色台词与动作描述更容易区分。'}
            >
              <Toggle checked={dialogueHighlight} onChange={value => updatePreference('dialogueHighlight', value)} label={english ? 'Dialogue highlighting' : '对话高亮'} />
            </SettingsRow>
            <SettingsRow
              icon={Highlighter}
              title={english ? 'Highlight color' : '高亮颜色'}
              description={english ? 'Use muted presets that do not distract from reading.' : '使用低饱和预设，避免污染阅读。'}
            >
              <div className="creation-color-swatches" role="radiogroup" aria-label={english ? 'Dialogue highlight color' : '对话高亮颜色'}>
                {[
                  ['lavender', '#8b78a4'],
                  ['amber', '#a37748'],
                  ['blue', '#637f9a'],
                  ['green', '#66877a'],
                ].map(([value, color]) => (
                  <button
                    type="button"
                    role="radio"
                    aria-label={value}
                    aria-checked={highlightColor === value}
                    className={highlightColor === value ? 'is-active' : ''}
                    onClick={() => updatePreference('highlightColor', value)}
                    disabled={!dialogueHighlight}
                    style={{ '--swatch-color': color }}
                    key={value}
                  />
                ))}
              </div>
            </SettingsRow>
            <SettingsRow icon={CheckCircle2} title={english ? 'Format hints' : '格式提示'} description={english ? 'Check scene headings, character names, and dialogue formatting.' : '检查场景头、角色名和对白格式。'}>
              <Segmented
                label={english ? 'Format hint intensity' : '格式提示强度'}
                value={formatHints}
                options={[
                  { value: 'off', label: english ? 'Off' : '关闭' },
                  { value: 'gentle', label: english ? 'Gentle' : '轻提示' },
                  { value: 'standard', label: english ? 'Standard' : '标准' },
                ]}
                onChange={value => updatePreference('formatHints', value)}
              />
            </SettingsRow>
            <SettingsRow icon={Focus} title={english ? 'Focus mode by default' : '默认专注模式'} description={english ? 'Collapse nonessential panels when entering the script.' : '进入正文时收起非必要面板。'}>
              <Toggle checked={focusMode} onChange={value => updatePreference('focusMode', value)} label={english ? 'Focus mode by default' : '默认专注模式'} />
            </SettingsRow>
            <SettingsRow icon={PanelTop} title={english ? 'Confirm before closing unsaved tabs' : '关闭未保存标签前确认'} description={english ? 'Prevent closing a script that is still saving or failed to save.' : '防止误关仍在保存或保存失败的正文。'}>
              <Toggle checked={confirmClose} onChange={value => updatePreference('confirmClose', value)} label={english ? 'Confirm before closing unsaved tabs' : '关闭未保存标签前确认'} />
            </SettingsRow>
          </SettingsCard>
        </div>

        <aside className={`creation-editor-preview is-${background} is-font-${font}`}>
          <div className="creation-editor-preview__top">
            <span>{english ? 'Episode 12 · Script preview' : '第 12 集 · 正文预览'}</span>
            <em>{fontSize}px / {lineHeight}</em>
          </div>
          <div style={{ fontSize: `${fontSize}px`, lineHeight }}>
            <p className="is-scene">{english ? 'SCENE 1: OLD THEATER BACKSTAGE · NIGHT' : '场景一：旧剧院后台 · 夜'}</p>
            <p>{english ? 'Rain muffles footsteps at the end of the corridor.' : '雨声压住了走廊尽头的脚步。'}</p>
            <p
              className={dialogueHighlight ? `is-dialogue is-${highlightColor}` : ''}
            >
              {english ? 'LIN QIAO: “This letter did not appear today.”' : '林乔：“这封信不是今天才出现的。”'}
            </p>
            <p>{english ? 'She turns it over, revealing a faded date.' : '她翻到背面，褪色的日期露了出来。'}</p>
          </div>
          <footer>
            <Eye size={13} />
            {english ? 'Live preview. Script content is unchanged.' : '实时预览，不改变正文内容'}
          </footer>
        </aside>
      </div>

      <SettingsScopeNote>
        {english ? 'Export fonts and delivery layout belong to export templates and do not follow personal editor preferences.' : '导出字体和交付排版属于导出模板，不跟随个人编辑器偏好。'}
      </SettingsScopeNote>
    </div>
  );
}
